import { useState } from 'react';
import { Badge } from '../components/ui/Badge';
import { Button } from '../components/ui/Button';
import { Card, CardHeader, CardTitle } from '../components/ui/Card';
import { EstadoError, EstadoTodoBien, EstadoVacio, ListaEsqueleto } from '../components/ui/Estados';
import { Input, Select } from '../components/ui/Input';
import { Modal } from '../components/ui/Modal';
import { StatusDot } from '../components/ui/StatusDot';
import { Tabla, type Columna } from '../components/ui/Tabla';
import { Criticidad } from '../../domain/value-objects/Criticidad';
import { Dominio } from '../../domain/value-objects/Dominio';
import estilos from './SistemaDiseno.module.css';

/** Variables de superficie que conviene revisar al cambiar de tema. */
const SUPERFICIES = [
  { nombre: 'Fondo', token: '--bg' },
  { nombre: 'Superficie', token: '--surface' },
  { nombre: 'Tarjeta', token: '--card' },
  { nombre: 'Elevada', token: '--superficie-elevada' },
  { nombre: 'Acento', token: '--accent' },
];

interface FilaEjemplo {
  id: number;
  sensor: string;
  nodo: string;
  valor: string;
  estado: string;
}

const FILAS: FilaEjemplo[] = [
  { id: 1, sensor: 'Temperatura cava', nodo: 'Sala Técnica', valor: '4.2 °C', estado: 'Normal' },
  { id: 2, sensor: 'Nivel combustible', nodo: 'Planta', valor: '12 %', estado: 'Crítico' },
  { id: 3, sensor: 'Puerta depósito', nodo: 'Acceso Norte', valor: 'ON', estado: 'Normal' },
];

const COLUMNAS: Columna<FilaEjemplo>[] = [
  {
    clave: 'sensor',
    encabezado: 'Sensor',
    render: (f) => f.sensor,
    ordenable: true,
    movil: 'titulo',
  },
  { clave: 'nodo', encabezado: 'Nodo', render: (f) => f.nodo, movil: 'subtitulo' },
  { clave: 'valor', encabezado: 'Valor', render: (f) => f.valor, alinear: 'derecha' },
  {
    clave: 'estado',
    encabezado: 'Estado',
    render: (f) => (
      <Badge color={f.estado === 'Crítico' ? 'var(--sev-critica)' : 'var(--status-success)'}>
        {f.estado}
      </Badge>
    ),
  },
];

/**
 * Muestrario del sistema visual.
 *
 * Sirve para comprobar de un vistazo que un cambio de tema no rompe nada: si
 * esta pantalla se ve bien en claro y en oscuro, el resto de la aplicación
 * también, porque todos los componentes salen de aquí.
 */
export function SistemaDiseno() {
  const [busqueda, setBusqueda] = useState('');
  const [modalAbierto, setModalAbierto] = useState(false);

  return (
    <div className={estilos.contenido}>
      <div>
        <h1>Sistema de diseño</h1>
        <p style={{ color: 'var(--text-2)', marginTop: '0.25rem' }}>
          Todos los componentes con el tema activo. Cambiar de tema es editar una línea en{' '}
          <code>src/main.tsx</code>.
        </p>
      </div>

      <section className={estilos.seccion}>
        <h2 className={estilos.tituloSeccion}>Superficies</h2>
        <div className={estilos.rejilla}>
          {SUPERFICIES.map((s) => (
            <div
              key={s.token}
              className={estilos.muestra}
              style={{ background: `var(${s.token})`, color: 'var(--text-1)' }}
            >
              <span className={estilos.nombreMuestra}>{s.nombre}</span>
              <span className={estilos.valorMuestra}>{s.token}</span>
            </div>
          ))}
        </div>
      </section>

      <section className={estilos.seccion}>
        <h2 className={estilos.tituloSeccion}>Severidad y dominios</h2>
        <div className={estilos.fila}>
          {Criticidad.todas().map((c) => (
            <Badge key={c.valor} color={c.color} grande>
              {c.etiqueta}
            </Badge>
          ))}
        </div>
        <div className={estilos.fila}>
          {Dominio.todos().map((d) => (
            <Badge key={d.valor} color={d.color} grande conPunto>
              {d.etiqueta}
            </Badge>
          ))}
        </div>
        <div className={estilos.fila}>
          <StatusDot color="var(--status-success)" encendido texto="Conectado" tamano={12} />
          <StatusDot color="var(--status-danger)" texto="Desconectado" tamano={12} />
          <StatusDot color="var(--text-3)" texto="Sin latido" tamano={12} />
        </div>
      </section>

      <section className={estilos.seccion}>
        <h2 className={estilos.tituloSeccion}>Botones</h2>
        <div className={estilos.fila}>
          <Button icono="agregar">Crear</Button>
          <Button variante="secundario" icono="refrescar">
            Actualizar
          </Button>
          <Button variante="fantasma" icono="filtrar">
            Filtros
          </Button>
          <Button variante="exito" icono="encender">
            Encender
          </Button>
          <Button variante="peligro" icono="apagar">
            Apagar
          </Button>
          <Button cargando>Enviando</Button>
          <Button
            variante="secundario"
            icono="candado"
            motivoDeshabilitado="Tu usuario no tiene habilitado el control de actuadores"
          >
            Sin permiso
          </Button>
          <Button variante="secundario" onClick={() => setModalAbierto(true)}>
            Abrir modal
          </Button>
        </div>
      </section>

      <section className={estilos.seccion}>
        <h2 className={estilos.tituloSeccion}>Tarjetas y tabla</h2>
        <Card>
          <CardHeader>
            <CardTitle subtitulo="Área Sur · Cliente Omsaica">Nodo Portón Principal</CardTitle>
            <StatusDot color="var(--status-success)" encendido texto="Conectado" tamano={10} />
          </CardHeader>
          <div className={estilos.fila}>
            <Badge color="var(--domain-seguridad)" conPunto>
              Seguridad
            </Badge>
            <Badge color="var(--text-2)">4 sensores</Badge>
            <Badge color="var(--text-2)">2 actuadores</Badge>
          </div>
        </Card>

        <Tabla
          datos={FILAS}
          columnas={COLUMNAS}
          claveFila={(f) => f.id}
          etiqueta="Ejemplo de tabla de sensores"
        />
      </section>

      <section className={estilos.seccion}>
        <h2 className={estilos.tituloSeccion}>Formularios</h2>
        <Card>
          <div className={estilos.formulario}>
            <Input
              etiqueta="Buscar"
              icono="buscar"
              placeholder="Nombre del sensor o nodo"
              value={busqueda}
              onChange={(e) => setBusqueda(e.target.value)}
            />
            <Select
              etiqueta="Cliente"
              placeholder="Todos los clientes"
              opciones={[
                { valor: '1', etiqueta: 'Omsaica' },
                { valor: '2', etiqueta: 'Zona Electrónica' },
              ]}
            />
            <Input etiqueta="Contraseña" alternarVisibilidad ayuda="Mínimo 8 caracteres" />
            <Input
              etiqueta="Umbral máximo"
              type="number"
              error="Debe ser mayor que el umbral mínimo"
              defaultValue={10}
            />
          </div>
        </Card>
      </section>

      <section className={estilos.seccion}>
        <h2 className={estilos.tituloSeccion}>Estados de pantalla</h2>
        <div className={estilos.estados}>
          <EstadoTodoBien />
          <EstadoVacio
            titulo="Sin sensores en esta área"
            descripcion="Cuando agregues un nodo con sensores aparecerán aquí."
            acciones={<Button icono="agregar">Agregar nodo</Button>}
          />
          <EstadoError
            acciones={
              <Button variante="secundario" icono="refrescar">
                Reintentar
              </Button>
            }
          />
        </div>
        <ListaEsqueleto filas={3} />
      </section>

      <Modal
        abierto={modalAbierto}
        onCerrar={() => setModalAbierto(false)}
        titulo="Ejemplo de modal"
        descripcion="En pantallas pequeñas se ancla abajo, al alcance del pulgar."
        pie={
          <>
            <Button variante="secundario" onClick={() => setModalAbierto(false)}>
              Cancelar
            </Button>
            <Button onClick={() => setModalAbierto(false)}>Guardar</Button>
          </>
        }
      >
        <div className={estilos.formulario}>
          <Input etiqueta="Nombre" placeholder="Nombre del nodo" />
          <Select
            etiqueta="Área"
            placeholder="Selecciona un área"
            opciones={[{ valor: '1', etiqueta: 'Área Sur' }]}
          />
        </div>
      </Modal>
    </div>
  );
}
